import React, { useEffect, useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { Gift, giftApi } from '../../services/giftApi';
import FloatingLoginModal from '../../components/GiftShop/FloatingLoginModal';
import { Helmet } from 'react-helmet-async';
import { useToast } from '../../contexts/ToastContext';
import { useGiftCart } from '../../contexts/GiftCartContext';
import { usePricing } from '../../contexts/PricingContext';
import { API_BASE_URL } from '../../config/apiConfig';
import CachedImage from '../../components/Common/CachedImage';
import { settingsApi } from '../../services/settingsApi';

const GiftShop: React.FC = () => {
    const { addToCart, totalItems: totalCartItems, setIsCartOpen } = useGiftCart();
    const [gifts, setGifts] = useState<Gift[]>([]);
    const [loading, setLoading] = useState(true);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [hasAuth, setHasAuth] = useState(false);
    const { showInfo } = useToast();
    const { displayCurrency, exchangeRates, setCurrency } = usePricing();

    const [backendCategories, setBackendCategories] = useState<string[]>([]);

    useEffect(() => {
        fetchGifts();
        fetchSettings();
        const token = localStorage.getItem('authToken');
        setHasAuth(!!token);

        // Polling for automatic reflection of admin changes
        const interval = setInterval(() => {
            fetchGifts();
            fetchSettings();
        }, 30000); // Poll every 30 seconds
        return () => clearInterval(interval);
    }, []);

    const fetchSettings = async () => {
        try {
            const settings = await settingsApi.getSettings();
            if (settings.gift_category_order) {
                setBackendCategories(settings.gift_category_order);
                localStorage.setItem('custom_gift_categories', JSON.stringify(settings.gift_category_order));
            }
        } catch (e) {
            console.error('Failed to fetch gift settings', e);
        }
    };

    const fetchGifts = async () => {
        try {
            // Add timestamp to bypass potential caching
            const response = await giftApi.getAllGifts(true);
            setGifts(Array.isArray(response) ? response : []);
        } catch (e) {
            console.error('Failed to load gifts', e);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckout = async () => {
        setIsCartOpen(true);
    };

    // Bind correctly to Vercel/Render production proxy gateway bypassing local env strings
    const API_URL = API_BASE_URL.replace(/\/api$/, '');

    const activeCategory = 'All';
    // No category tabs on GiftShop subdomain
    
    // Use backend categories if available, otherwise fallback to local
    const customCategories: string[] = backendCategories.length > 0 ? backendCategories : (() => {
        const customCatString = localStorage.getItem('custom_gift_categories');
        let rawCustom = [];
        try {
            rawCustom = customCatString ? JSON.parse(customCatString) : [];
        } catch(e) {}
        return Array.isArray(rawCustom) ? rawCustom : [];
    })();

    // Derive used categories from gifts - Guarded against undefined gifts
    const extractedCategories = Array.isArray(gifts) 
        ? Array.from(new Set(gifts.map(g => g.category || 'Uncategorized'))).filter(Boolean) as string[]
        : [];

    // Merge them while maintaining custom sequence priorities
    const categories = ['All', ...Array.from(new Set([...(customCategories || []), ...extractedCategories]))];
    
    // Filter and SORT gifts based on active tab and sortOrder - Guarded against undefined
    const filteredGifts = [...(Array.isArray(gifts) 
        ? (activeCategory === 'All' ? gifts : gifts.filter(g => (g.category || 'Uncategorized') === activeCategory))
        : [])]
        .sort((a, b) => {
            if (activeCategory === 'All') {
                const catAIndex = categories.indexOf(a.category || 'Uncategorized');
                const catBIndex = categories.indexOf(b.category || 'Uncategorized');
                if (catAIndex !== catBIndex) {
                    // Safe guard array extremes
                    return (catAIndex === -1 ? 999 : catAIndex) - (catBIndex === -1 ? 999 : catBIndex);
                }
            }
            return (a.sortOrder || 0) - (b.sortOrder || 0);
        });

    // Handle currency conversion
    const convertGiftPrice = (price: number, baseCurrency: string = 'USD') => {
        if (baseCurrency === displayCurrency) return price;
        
        // Convert to USD first (base rate logic)
        let usdAmount = price;
        if (baseCurrency !== 'USD') {
            const baseRate = exchangeRates[baseCurrency];
            if (baseRate) usdAmount = price / baseRate;
        }
        
        // Convert from USD to display currency
        const targetRate = exchangeRates[displayCurrency] || 1;
        return usdAmount * targetRate;
    };

    const currencyOptions = Object.keys(exchangeRates).length > 0 
        ? Object.keys(exchangeRates).sort() 
        : ['USD', 'KES', 'EUR', 'GBP', 'AUD'];

    return (
        <>
            <Helmet>
                <title>SightTrails Gift Shop – Premium Safari Gear</title>
                <meta name="description" content="Explore exclusive SightTrails gear and safari apparel. Premium, ethically sourced, and inspired by the African wilderness. Shop now!" />
                <meta property="og:title" content="SightTrails Gift Shop" />
                <meta property="og:description" content="Discover premium safari apparel and authentic African gear. Perfect for adventurers and wildlife lovers." />
                <meta property="og:url" content="https://giftshop.sighttrails.com" />
                <meta property="og:image" content="https://giftshop.sighttrails.com/og-image.jpg" />
                <meta property="og:type" content="website" />
            </Helmet>
            <div className="min-h-screen bg-gray-50/50 py-12 pt-4">
                <FloatingLoginModal
                    isOpen={showLoginModal}
                    onClose={() => setShowLoginModal(false)}
                onSuccess={() => {
                    setHasAuth(true);
                    setShowLoginModal(false);
                    // trigger checkout immediately after auth
                    setTimeout(() => handleCheckout(), 500);
                }}
            />

            {/* Note: Global Floating Cart is now handled by FloatingHub */}

            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {/* Refined Header Section */}
                <div className="relative mb-12 rounded-3xl p-10 md:p-14 overflow-hidden bg-white border border-gray-100 shadow-[0_15px_40px_rgba(0,0,0,0.06)]">
                    <div className="relative z-10 text-center space-y-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 mb-2">
                            <ShoppingCart className="w-4 h-4" />
                            <span className="text-[10px] font-bold uppercase tracking-widest">Official SightTrails Gear</span>
                        </div>

                        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-none">
                            <span className="text-[#2A7ECE]">Sight</span>
                            <span className="text-slate-900">Trails</span>
                            <span className="text-slate-900 ml-4">Gift Shop</span>
                        </h1>

                        <p className="max-w-2xl mx-auto text-lg md:text-xl text-slate-600 font-medium leading-relaxed">
                            Premium safari apparel and authentic savannah gear. <br className="hidden md:block" />
                            Exclusively designed for the bold explorer, inspired by the African wilderness.
                        </p>
                    </div>
                </div>                {/* No category tabs or filter on the GiftShop subdomain – a clean, focused shop layout */}
                {/* Simple currency picker kept for price conversion */}
                <div className="flex items-center justify-end mb-6 w-full">
                    <div className="flex items-center bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm w-auto hover:border-gray-300 transition-colors relative">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-1.5">Currency</span>
                        <div className="relative flex items-center">
                            <select
                                value={displayCurrency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="bg-transparent text-xs font-black text-slate-800 outline-none cursor-pointer appearance-none pr-4 z-10 w-auto"
                            >
                                {currencyOptions.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                            <svg className="absolute right-0 w-3 h-3 text-slate-400 pointer-events-none z-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7"/></svg>
                        </div>
                    </div>
                </div>

                {/* Floating Items Grid */}
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredGifts.length === 0 ? (
                            <div className="col-span-1 md:col-span-2 lg:col-span-3 py-16 text-center text-slate-500 font-medium">
                                No items found in this category.
                            </div>
                        ) : (
                            filteredGifts.map((gift) => (
                            <div
                                key={gift.id}
                                className="group relative flex flex-col items-center p-6 rounded-3xl bg-white border border-gray-100 shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-xl overflow-hidden"
                            >
                                {/* product Image Container */}
                                <div className="relative w-full h-64 mb-6 flex justify-center items-center">
                                    <CachedImage
                                        src={gift.imageUrl && gift.imageUrl.startsWith('http') ? gift.imageUrl : `${API_URL}${gift.imageUrl && gift.imageUrl.startsWith('/') ? '' : '/'}${gift.imageUrl || ''}`}
                                        alt={gift.name}
                                        className={`relative z-10 h-full object-contain transition-all duration-700 ease-in-out group-hover:scale-[1.03] ${
                                            !(gift.imageUrl && gift.imageUrl.includes('blur=true')) 
                                            ? 'rounded-[2rem] drop-shadow-[0_8px_20px_rgba(0,0,0,0.06)] group-hover:drop-shadow-[0_15px_35px_rgba(0,0,0,0.12)]' 
                                            : ''
                                        }`}
                                        loading="lazy"
                                        style={gift.imageUrl && gift.imageUrl.includes('blur=true') ? {
                                            maskImage: 'radial-gradient(circle, black 50%, transparent 90%)',
                                            WebkitMaskImage: 'radial-gradient(circle, black 50%, transparent 90%)'
                                        } : undefined}
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = 'https://placehold.co/400x400?text=Gift+Product';
                                        }}
                                    />
                                </div>

                                {/* Product Info */}
                                <div className="w-full flex-1 flex flex-col justify-between">
                                    <div className="mb-6">
                                        <h3 className="text-lg font-bold text-slate-800 text-center mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors">{gift.name}</h3>
                                        <p className="text-sm text-slate-500 text-center line-clamp-2 font-medium leading-relaxed px-2">{gift.description}</p>
                                    </div>

                                    <div className="flex justify-between items-center pt-4 border-t border-gray-50 mt-auto">
                                        <div className="flex flex-col">
                                            <span className="text-[10px] uppercase tracking-tighter text-slate-400 font-bold">Price</span>
                                            <span className="text-xl font-black text-slate-900 flex items-center gap-1">
                                                <span className="text-sm text-blue-600/60">{displayCurrency === 'USD' ? '$' : displayCurrency}</span>
                                                {convertGiftPrice(Number(gift.price), gift.currency || 'USD').toFixed(2)}
                                            </span>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); addToCart(gift); }}
                                            className="bg-slate-900 hover:bg-blue-600 text-white p-3 rounded-xl transition-all active:scale-95 shadow-md"
                                        >
                                            <ShoppingCart className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default GiftShop;
